import React, { useState, useMemo } from 'react';
import { FiX as X, FiSearch as Search } from 'react-icons/fi';
import { WIDGET_REGISTRY, WIDGET_CATEGORIES } from './widgetRegistry';
import { usePermissions } from '../../hooks/usePermissions';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { PERMISSIONS } from '../../utils/permissions';

interface Props {
  onSelect: (widgetId: string) => void;
  onClose: () => void;
  defaultCategory?: string | null;
}

const WidgetPicker: React.FC<Props> = ({ onSelect, onClose, defaultCategory = null }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>(defaultCategory ?? 'all');
  const { hasPermission, isAdmin } = usePermissions();
  const { isAWSUser } = useModuleAccess();

  const canSeeHealth = useMemo(
    () =>
      hasPermission(PERMISSIONS.AWS_ACCESS_VIEW) ||
      hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
      hasPermission(PERMISSIONS.PARTNERS_MANAGE) ||
      isAdmin ||
      isAWSUser(),
    [hasPermission, isAdmin, isAWSUser]
  );

  const canSeeAwsInsights = useMemo(
    () =>
      isAWSUser() ||
      hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
      hasPermission(PERMISSIONS.PARTNERS_MANAGE),
    [hasPermission, isAWSUser]
  );

  const awsInsightsCategory = (WIDGET_CATEGORIES as Record<string, string>).AWS_INSIGHTS;

  const filteredWidgets = WIDGET_REGISTRY.filter((widget: { name: string; description: string; category: string }) => {
    const matchesSearch = widget.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         widget.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || widget.category === selectedCategory;

    const hasAccess = (
      (widget.category !== WIDGET_CATEGORIES.HEALTH || canSeeHealth) &&
      (!awsInsightsCategory || widget.category !== awsInsightsCategory || canSeeAwsInsights)
    );

    return matchesSearch && matchesCategory && hasAccess;
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col transition-colors">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white transition-colors">
              Add Widget
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 transition-colors">
              Showing {filteredWidgets.length} widget{filteredWidgets.length === 1 ? '' : 's'} {selectedCategory !== 'all' && `in ${selectedCategory}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search widgets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All
            </button>
            {Object.values(WIDGET_CATEGORIES)
              .filter((category: string) => {
                if (category === 'Custom') return false;
                if (category === WIDGET_CATEGORIES.HEALTH && !canSeeHealth) return false;
                if (awsInsightsCategory && category === awsInsightsCategory && !canSeeAwsInsights) return false;
                return true;
              })
              .map((category: string) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {category}
                </button>
              ))}
          </div>
        </div>

        {/* Widget Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredWidgets.map((widget: {
              id: string;
              name: string;
              description: string;
              category: string;
              icon?: unknown;
              defaultSize: { w: number; h: number };
            }) => (
              <button
                key={widget.id}
                onClick={() => onSelect(widget.id)}
                className="text-left p-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8">
                    {typeof widget.icon === 'function' ? (
                      React.createElement(widget.icon as React.ComponentType<{ className: string }>, { className: 'w-6 h-6 text-blue-500 dark:text-blue-400' })
                    ) : typeof widget.icon === 'string' ? (
                      <span className="text-2xl">{widget.icon}</span>
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-white truncate transition-colors">
                      {widget.name}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2 transition-colors">
                      {widget.description}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded transition-colors">
                        {widget.category}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 transition-colors">
                        {widget.defaultSize.w}x{widget.defaultSize.h}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {filteredWidgets.length === 0 && (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 transition-colors">
                No widgets found matching your search
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetPicker;
